import { currentDirection } from "@app/lib/helper";
import { URLS } from "@app/shared";
import { MarkSvg, SvgOverLayer } from "@assets/icons";
import { Button, Link } from "@design-system/components";
import { trans } from "@mongez/localization";
import { ChevronRight } from "lucide-react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { SectorData } from "../../data";

type OurSectorsSectionMobileProps = {
  data: SectorData[];
};

export function OurSectorsSectionMobile({
  data,
}: OurSectorsSectionMobileProps) {
  const dir = currentDirection();

  return (
    <section className="py-8 lg:hidden">
      <div className="container">
        <h2 className="mb-8 text-clamp-title font-semibold text-white">
          {trans("ourSectors.title")}
        </h2>
      </div>

      <Swiper
        className="our-sectors-mobile w-full pb-10"
        spaceBetween={16}
        slidesPerView={1.2}
        centeredSlides={false}
        pagination={{
          dynamicBullets: true,
          clickable: true,
        }}
        modules={[Pagination]}
        dir={dir}
        key={dir}
        breakpoints={{
          375: {
            slidesPerView: 1.2,
            spaceBetween: 16,
          },
          640: {
            slidesPerView: 1.5,
            spaceBetween: 20,
          },
          768: {
            slidesPerView: 2,
            spaceBetween: 24,
          },
        }}
      >
        {data.map((sector, index) => (
          <SwiperSlide key={index}>
            <div className="relative mx-4 h-[400px] overflow-hidden rounded-lg sm:h-[450px]">
              {/* Background Image */}
              <img
                src={sector.img}
                alt={trans(sector.titleKey)}
                className="h-full w-full object-cover"
              />

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

              {/* MarkSvg and Color Overlay */}
              <div className="absolute inset-0">
                <MarkSvg />
                <SvgOverLayer
                  color={sector.color}
                  id={`sector_mobile_${index}`}
                />
              </div>

              {/* Content */}
              <div className="absolute inset-0 flex flex-col justify-end p-4 text-white">
                <div
                  className="mb-4 h-[4px] w-full max-w-[200px] rounded-full"
                  style={{ backgroundColor: sector.color }}
                />
                <h3 className="mb-2 text-xl font-bold sm:text-2xl">
                  {trans(sector.titleKey)}
                </h3>
                <p className="mb-4 line-clamp-4 text-sm font-medium leading-relaxed sm:text-base">
                  {trans(sector.descriptionKey)}
                </p>

                <Link to={URLS.sectors.view(sector.to)}>
                  <Button
                    variant="outline"
                    className="h-10 w-32 min-w-full gap-2 rounded-full bg-[#FFFFFF29] text-xs font-bold uppercase text-white hover:bg-[#FFFFFF29] sm:h-12 sm:w-36 sm:text-sm"
                    rightIcon={
                      <ChevronRight className="h-4 w-4 rtl:rotate-180" />
                    }
                  >
                    {trans("ourSectors.learnMore")}
                  </Button>
                </Link>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
}
